import { z } from 'zod';
import { uuidSchema, currencyCodeSchema } from './common';
import { transactionSubtypeSchema } from './transactions';

/**
 * 💬 Sohbet (konuşarak işlem) — kullanıcı serbest metin yazar, Claude işlemi çıkarır.
 * conversationId yoksa yeni konuşma açılır.
 */
export const chatSendSchema = z.object({
  conversationId: uuidSchema.optional(),
  // Toplu işlem (batch) tek mesajda 25 kaleme kadar çıkarılabiliyor → ~20+ satırlık liste
  // 1000 karakteri kolayca aşıyor. Sınır, batch tasarımıyla uyumlu olacak şekilde yükseltildi.
  message: z.string().min(1).max(4000),
});
export type ChatSendInput = z.infer<typeof chatSendSchema>;

/** Konuşmayı yeniden adlandır (başlık). `.trim()` → yalnız boşluktan ibaret başlık reddedilir. */
export const chatRenameSchema = z.object({
  title: z.string().trim().min(1).max(200),
});
export type ChatRenameInput = z.infer<typeof chatRenameSchema>;

/** Tutar string'i (ör. "12.34"). */
const chatAmountSchema = z.string().regex(/^\d+(\.\d{1,2})?$/, 'Geçersiz tutar (ör. "12.34")');

/**
 * 🔁 Sohbet recurring bilgisi (taksit/kredi ya da süresiz düzenli ödeme). Taslak→commit'te
 * korunması için commit gövdesine eklenir (oto-commit yolunda zaten aktarılıyordu).
 */
export const chatRecurringSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('installment'),
    total: z.number().int().min(2),
    current: z.number().int().min(1).nullable(),
  }),
  z.object({
    kind: z.literal('repeat'),
    intervalMonths: z.number().int().min(1).max(24),
  }),
]);
export type ChatRecurringInput = z.infer<typeof chatRecurringSchema>;

/**
 * 💸 Sohbet split payment parçası: bir gideri birden çok para hesabına (moneyAccountId) ya da
 * başka bir karta (paymentCardId, Faz 3b — mirror işlem) bölmek için. Tam olarak biri verilir;
 * ana entry bu parçaların toplamı kadar net'e iner.
 */
export const chatSplitPaymentSchema = z
  .object({
    moneyAccountId: uuidSchema.optional(),
    paymentCardId: uuidSchema.optional(),
    amount: chatAmountSchema,
  })
  .refine((d) => Boolean(d.moneyAccountId) !== Boolean(d.paymentCardId), {
    message: 'moneyAccountId VEYA paymentCardId, sadece biri belirtilmelidir',
    path: ['moneyAccountId'],
  });
export type ChatSplitPaymentInput = z.infer<typeof chatSplitPaymentSchema>;

/**
 * Taslağı onayla/commit et: AI güven düşükse ya da kart belirsizse kullanıcı UI'da
 * kartı (ve istenirse hesabı) seçip bu uçla gönderir → gerçek transaction oluşur.
 */
export const chatCommitSchema = z.object({
  conversationId: uuidSchema,
  cardId: uuidSchema,
  name: z.string().min(1).max(200),
  amount: chatAmountSchema,
  currency: currencyCodeSchema.optional(),
  date: z.string().date().optional(),
  subtype: transactionSubtypeSchema,
  moneyAccountId: uuidSchema.optional(),
  tagIds: z.array(uuidSchema).max(10).optional(),
  /** 📝 Serbest not / açıklama (Faz 2). */
  description: z.string().max(1000).optional(),
  /** 🇪🇺 KDV / iş gideri (Faz 2): işaretliyse vatRate ile birlikte business expense olarak işlenir. */
  isBusinessExpense: z.boolean().optional(),
  vatRate: z.number().min(0).max(100).optional(),
  /** 💸 Açık takas (Faz 3): gider moneyAccountId'den görünür ama gerçekte settlementAccountId'den çekilir. */
  settlementAccountId: uuidSchema.optional(),
  settlementAmount: z
    .string()
    .regex(/^\d+(\.\d{1,2})?$/)
    .optional(),
  settlementDate: z.string().date().optional(),
  /** 🏠 Ortak gider (Faz 3c): household grubu işareti (reports scope). */
  householdGroupId: uuidSchema.optional(),
  /** 💱 Döviz kuru override (Faz 3c): yabancı para girişinde manuel kur (base'e). Yoksa otomatik çözülür. */
  exchangeRate: z
    .string()
    .regex(/^\d+(\.\d{1,6})?$/)
    .optional(),
  /** ⏰ Tek-seferlik zamanlı oto-onay: dueDate gününde otomatik onaylanır (autoConfirmOnDue). */
  dueDate: z.string().date().optional(),
  autoConfirmOnDue: z.boolean().optional(),
  /** Çoklu hesap bölüşümü: ana `moneyAccountId` dışındaki ek parçalar. */
  splitPayments: z.array(chatSplitPaymentSchema).max(9).optional(),
  /** Taksit/kredi ya da düzenli ödeme — taslaktan commit'te de korunur. */
  recurring: chatRecurringSchema.optional(),
  /** Idempotency: taslakla birlikte verilen tek-kullanımlık token (çift commit → tek işlem). */
  draftToken: uuidSchema.optional(),
});
export type ChatCommitInput = z.infer<typeof chatCommitSchema>;

/**
 * 🛑 Yıkıcı işlem (sil/düzenle) onayı: `send` bir `pendingAction` döndürür; kullanıcı UI'da
 * onaylar/iptal eder ve bu uçla gönderir. `token` pending'i tanımlar (replay/yanlış-hedef koruması).
 */
export const chatConfirmActionSchema = z.object({
  conversationId: uuidSchema,
  messageId: uuidSchema,
  token: uuidSchema,
  decision: z.enum(['confirm', 'cancel']),
});
export type ChatConfirmActionInput = z.infer<typeof chatConfirmActionSchema>;

/**
 * 🆕 Etkileşimli "pending prompt" çözümü (Faz 1+): refund/confirm/unconfirm onayı, hesap/kategori
 * oluşturma izni veya çoktan-seçmeli kategori seçimi. UI butonu bu uçla; sözlü ("evet/hayır/2")
 * yanıt ise normal send() içinde sınıflandırılır. `choiceIndex` yalnız decision='choice' için.
 */
export const chatResolvePromptSchema = z.object({
  conversationId: uuidSchema,
  messageId: uuidSchema,
  token: uuidSchema,
  decision: z.enum(['affirm', 'deny', 'choice']),
  choiceIndex: z.number().int().min(0).max(50).optional(),
  /**
   * 🆕 batchReview (Faz 6): kalem-başı kategori seçimi (UI). Her giriş bir inceleme kalemini
   * (`itemIndex`) bir kategoriye bağlar: `cardId` → dropdown'dan seçilen MEVCUT kategori (tip-uyumlu
   * tüm kategoriler arasından, whitelist = prompt.cards); `choiceIndex` → kalemin `choices`'ındaki
   * "yeni kategori" önerisinin indeksi. cardId verildiyse o önceliklidir. decision='affirm' ile gönderilir;
   * verilmeyen kalemler varsayılan (choices[0]) kalır.
   */
  selections: z
    .array(
      z.object({
        itemIndex: z.number().int().min(0).max(50),
        cardId: uuidSchema.optional(),
        choiceIndex: z.number().int().min(0).max(50).optional(),
      }),
    )
    .max(50)
    .optional(),
});
export type ChatResolvePromptInput = z.infer<typeof chatResolvePromptSchema>;

/**
 * ↩️ Son işlemi geri al: bir asistan mesajının `meta.undo`'suna göre oluşturulan işlemi siler
 * (create undo → softDelete) ya da silineni geri yükler (delete undo → restore; yalnız basit işlem).
 */
export const chatUndoSchema = z.object({
  conversationId: uuidSchema,
  messageId: uuidSchema,
});
export type ChatUndoInput = z.infer<typeof chatUndoSchema>;
